import {
    DxfWriter,
    point2d,
    HatchPolylineBoundary,
    HatchBoundaryPaths,
    HatchPredefinedPatterns,
    vertex,
    pattern,

} from '@tarikjabiri/dxf';

const defaultLayerName = 'export';

// list of fancy AutoCAD color indices
const colorIndices = [
    1,
    2,
    3,
    4,
    5,
    6,
    12,
    30,
    82,
    150,
    200
];

/**
 * Polygons are added as hatch entities
 */
function addPolygonToDxf(dxf, feature, layerName) {
    // boundary paths contains outer and inner rings
    const boundary = new HatchBoundaryPaths();

    const rings = feature.geometry.coordinates;
    for (const ring of rings) {
        const polyline = new HatchPolylineBoundary();

        for (const point of ring) {
            polyline.add(vertex(point[0], point[1]));
        }

        boundary.addPolylineBoundary(polyline);
    }

    // solid hatch colors
    const solid = pattern({
        name: HatchPredefinedPatterns.SOLID
    });

    dxf.addHatch(boundary, solid, {
        layerName: layerName,
    });
}


/**
 * MultiPolygons are added as hatch entities
 * These are added as multiple hatches (not one entity)
 */
function addMultiPolygonToDxf(dxf, feature, layerName) {
    const polygons = feature.geometry.coordinates;

    for (const polygon of polygons) {
        // boundary paths contains outer and inner rings
        const boundary = new HatchBoundaryPaths();

        for (const ring of polygon) {
            const polyline = new HatchPolylineBoundary();

            for (const point of ring) {
                polyline.add(vertex(point[0], point[1]));
            }

            boundary.addPolylineBoundary(polyline);
        }

        // solid hatch colors
        const solid = pattern({
            name: HatchPredefinedPatterns.SOLID
        });

        dxf.addHatch(boundary, solid, {
            layerName: layerName,
        });
    }
}


/**
 * Linestrings are added as polyline entities.
 * Only line segments, no arcs
 */
function addLineStringToDxf(dxf, feature, layerName) {
    const vertices = [];

    for (const point of feature.geometry.coordinates) {
        vertices.push({
            point: point2d(point[0], point[1]),
        });
    }

    const polyline = dxf.addLWPolyline(vertices);
    polyline.layerName = layerName;
}


/**
 * Multi linestrings are added as multiple polyline entities.
 * Only line segments, no arcs
 */
function addMultiLineStringToDxf(dxf, feature, layerName) {
    const linestrings = feature.geometry.coordinates;

    for (const linestring of linestrings) {
        const vertices = [];

        for (const point of linestring) {
            vertices.push({
                point: point2d(point[0], point[1]),
            });
        }

        const polyline = dxf.addLWPolyline(vertices);
        polyline.layerName = layerName;
    }
}


/**
 * Points are added as point entities
 * Point presentation is set in AutoCAD
 */
function addPointToDxf(dxf, feature, layerName) {
    const position = feature.geometry.coordinates;
    const point = dxf.addPoint(position[0], position[1]);
    point.layerName = layerName;
}


function addFeatureToDxf(dxf, feature, layerName) {
    const geometryType = feature.geometry.type;

    if (geometryType === 'MultiPolygon') {
        addMultiPolygonToDxf(dxf, feature, layerName);
    } else if (geometryType === 'Polygon') {
        addPolygonToDxf(dxf, feature, layerName);
    } else if (geometryType === 'MultiLineString') {
        addMultiLineStringToDxf(dxf, feature, layerName);
    } else if (geometryType === 'LineString') {
        addLineStringToDxf(dxf, feature, layerName);
    } else if (geometryType === 'Point') {
        addPointToDxf(dxf, feature, layerName);
    }
}


function getAutoCADColorIndex() {
    const maxACI = colorIndices.length;
    // S2245: Using pseudorandom number generators (PRNGs) is security-sensitive
    // Only generating a random array index => no cryptographically insecure code
    const ix = Math.floor(Math.random() * maxACI);//NOSONAR
    return colorIndices[ix];
}


export function dxfExport(geoJson, layerName) {
    // standaard layer lookup
    const layers = {
        'MultiPolygon': `${layerName}-multi-polygon`,
        'Polygon': `${layerName}-polygon`,
        'LineString': `${layerName}-linestring`,
        'MultiLineString': `${layerName}-multi-linestring`,
        'Point': `${layerName}-point`,
    };

    const dxf = new DxfWriter();

    // list of layers added to dxf
    const dxfLayers = [];

    for (const feature of geoJson.features) {
        let layerName = defaultLayerName;

        // check for a layer attribute
        if ('layer' in feature.properties) {
            layerName = feature.properties.layer;
        } else {
            const geometryType = feature.geometry.type;

            // get layername
            if (geometryType in layers) {
                layerName = layers[geometryType];
            }
        }

        // create layer if not exists
        if (!dxfLayers.includes(layerName)) {
            let colorIndex = getAutoCADColorIndex();
            // add layer with default properties
            dxf.addLayer(layerName, colorIndex, 'CONTINUOUS');
            dxfLayers.push(layerName);
        }

        // add feature to dxf file
        addFeatureToDxf(dxf, feature, layerName);
    }
    return dxf.stringify();
}

